/**
 * Hook to update the payload for the assigned and unassigned drivers
 * @param assignedPayload - The payload for the assigned drivers
 * @param unassignedPayload - The payload for the unassigned drivers
 * @param setAssignedPayload - The function to set the assigned payload
 * @param setUnassignedPayload - The function to set the unassigned payload
 * @param messageTypeForDriver - The message type for the driver
 */
import { useCallback } from 'react';
import { OrderSharePreviewParams, MESSAGE_TYPE } from '../../../redux/api/orders/orders.model';

interface UseDriverPayloadProps {
  assignedPayload: OrderSharePreviewParams[];
  unassignedPayload: OrderSharePreviewParams[];
  setAssignedPayload: (payload: OrderSharePreviewParams[]) => void;
  setUnassignedPayload: (payload: OrderSharePreviewParams[]) => void;
  messageTypeForDriver: MESSAGE_TYPE;
}

const useDriverPayload = ({
  assignedPayload,
  unassignedPayload,
  setAssignedPayload,
  setUnassignedPayload,
  messageTypeForDriver
}: UseDriverPayloadProps): {
  updatePayloadForAssigned: (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number,
    textMessage?: string
  ) => void;
  updatePayloadForUnassigned: (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number,
    textMessage?: string
  ) => void;
} => {
  const updateLoadSharePreviewList = useCallback(
    (
      id: string,
      radio: string,
      segmentId: string,
      removed: boolean,
      list: OrderSharePreviewParams[],
      messageType: MESSAGE_TYPE,
      oldDriverId?: string,
      driverName?: string,
      deletedSegmentIndex?: number,
      textMessage?: string
    ): OrderSharePreviewParams[] => {
      let finalList: OrderSharePreviewParams[] | undefined = list;

      if (removed) {
        finalList = finalList?.filter((segmentDriverInfo) => segmentDriverInfo.driver_id !== id);
      }

      if (oldDriverId) {
        finalList = finalList?.filter(
          (segmentDriverInfo) => segmentDriverInfo.driver_id !== oldDriverId
        );
      }

      const findDriver = finalList?.find(
        (segmentDriverInfo) =>
          segmentDriverInfo.driver_id === id && segmentDriverInfo.trip_id === segmentId
      );

      if (!findDriver && !removed) {
        finalList = [
          ...finalList,
          {
            driver_id: id,
            driver_name: driverName,
            trip_id: segmentId,
            via_sms: radio === 'phone',
            via_email: radio === 'email',
            message_type: messageType,
            deleted_trip_index: deletedSegmentIndex,
            text_message: textMessage
          }
        ];
      } else {
        finalList = finalList?.map((segmentDriverInfo) => {
          if (segmentDriverInfo.driver_id === id && segmentDriverInfo.trip_id === segmentId) {
            return {
              ...segmentDriverInfo,
              trip_id: segmentId,
              driver_name: driverName,
              via_sms: radio === 'phone',
              via_email: radio === 'email',
              deleted_trip_index: deletedSegmentIndex,
              text_message: textMessage
            };
          }
          return { ...segmentDriverInfo };
        });
      }

      return finalList;
    },
    []
  );

  const updatePayloadForAssigned = useCallback(
    (
      id: string,
      radio: string,
      segmentId: string,
      removed: boolean,
      oldDriverId?: string,
      driverName?: string,
      deletedSegmentIndex?: number,
      textMessage?: string
    ): void => {
      if (assignedPayload?.length > 0) {
        const newList = updateLoadSharePreviewList(
          id,
          radio,
          segmentId,
          removed,
          assignedPayload,
          messageTypeForDriver,
          oldDriverId,
          driverName,
          undefined,
          textMessage
        );
        setAssignedPayload(newList);
      }
    },
    [assignedPayload, messageTypeForDriver, setAssignedPayload, updateLoadSharePreviewList]
  );

  const updatePayloadForUnassigned = useCallback(
    (
      id: string,
      radio: string,
      segmentId: string,
      removed: boolean,
      oldDriverId?: string,
      driverName?: string,
      deletedSegmentIndex?: number,
      textMessage?: string
    ): void => {
      if (unassignedPayload?.length > 0) {
        const newList = updateLoadSharePreviewList(
          id,
          radio,
          segmentId,
          removed,
          unassignedPayload,
          MESSAGE_TYPE.UNASSIGNED,
          oldDriverId,
          driverName,
          deletedSegmentIndex,
          textMessage
        );
        setUnassignedPayload(newList);
      }
    },
    [unassignedPayload, setUnassignedPayload, updateLoadSharePreviewList]
  );

  return {
    updatePayloadForAssigned,
    updatePayloadForUnassigned
  };
};

export default useDriverPayload;
